import React, { FC, useContext } from 'react';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { useParams } from 'react-router-dom';
import { PolicyHolderType } from '@clearcover/core-app-types';
import classNames from 'classnames';

import {
  Row,
  Column,
  FormInput,
  FormModalHeader,
  FormModalContent,
  FormModalFooter,
} from 'Shared/components';
import { useLazyApi } from 'Shared/services/Api';

import styles from './styles.module.css';
import { formatPhoneNumber } from 'Shared/utils';
import { PolicyContext } from 'Policies/utils';
import { yupPhoneValidator } from 'Admin/utils/formValidators';

function PHONE_COMBO_VALIDATION(this: Yup.TestContext) {
  return this.parent.homePhone || this.parent.cellPhone;
}

const FORM_VALIDATION = Yup.object({
  name: Yup.object({
    firstName: Yup.string()
      .trim()
      .required('Required'),
    middleName: Yup.string().trim(),
    lastName: Yup.string()
      .trim()
      .required('Required'),
    suffix: Yup.string(),
  }),
  contactInfo: Yup.object({
    homePhone: yupPhoneValidator().test(
      'oneOfRequired',
      'Home Phone or Cell Phone is required.',
      PHONE_COMBO_VALIDATION
    ),
    cellPhone: yupPhoneValidator().test(
      'oneOfRequired',
      'Home Phone or Cell Phone is required.',
      PHONE_COMBO_VALIDATION
    ),
    email: Yup.string()
      .email('Must be a valid email address')
      .required('Email address is required'),
  }),
});

export const EditAccountForm: FC<{
  policyHolder: PolicyHolderType;
  close: () => void;
}> = ({
  policyHolder: { name, contactInfo, identifier: policyHolderId },
  close,
}) => {
  const { id: policyId } = useParams<{ id: string }>();
  const { refetchHandler } = useContext(PolicyContext);

  const [, editAccount] = useLazyApi({
    method: 'POST',
    url: `/policies/${policyId}/policyHolder/${policyHolderId}/editAccountDetails`,
  });

  return (
    <Formik
      initialValues={{ name, contactInfo }}
      onSubmit={({ name, contactInfo }, { setStatus, setSubmitting }) => {
        editAccount({
          data: {
            name: {
              firstName: name.firstName.trim(),
              middleName: name.middleName.trim(),
              lastName: name.lastName.trim(),
              suffix: name.suffix,
            },
            contactInfo,
          },
        })
          .then(() => {
            close();
            refetchHandler();
          })
          .catch(({ message }) => {
            setSubmitting(false);
            setStatus(message);
          });
      }}
      validationSchema={FORM_VALIDATION}
      validateOnMount
    >
      {({
        handleChange,
        handleBlur,
        setFieldValue,
        values,
        touched,
        errors,
      }) => {
        return (
          <>
            <FormModalHeader>Edit Account Details</FormModalHeader>
            <FormModalContent>
              <div className={styles.section}>
                <h1>Account Details</h1>
                <Row>
                  <Column className={classNames('fs-exclude')}>
                    <FormInput
                      variant="text"
                      name="name.firstName"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.name.firstName}
                      errorMessage={
                        touched.name?.firstName
                          ? errors.name?.firstName
                          : undefined
                      }
                      label="First Name"
                      data-testid="first-name"
                    />
                  </Column>
                  <Column className={'fs-exclude'}>
                    <FormInput
                      variant="text"
                      name="name.middleName"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.name.middleName}
                      errorMessage={
                        touched.name?.middleName
                          ? errors.name?.middleName
                          : undefined
                      }
                      label="Middle Name"
                      data-testid="middle-name"
                    />
                  </Column>
                  <Column className={classNames('fs-exclude')}>
                    <FormInput
                      variant="text"
                      name="name.lastName"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.name.lastName}
                      errorMessage={
                        touched.name?.lastName
                          ? errors.name?.lastName
                          : undefined
                      }
                      label="Last Name"
                      data-testid="last-name"
                    />
                  </Column>
                  <Column>
                    <FormInput
                      variant="selectAutocomplete"
                      name="name.suffix"
                      data-testid="suffix"
                      onChange={(value: string) =>
                        setFieldValue(
                          'name.suffix',
                          value === 'none' ? '' : value
                        )
                      }
                      onBlur={handleBlur}
                      value={values.name.suffix}
                      label="Suffix"
                      options={{
                        none: '',
                        jr: 'Jr',
                        sr: 'Sr',
                        i: 'I',
                        ii: 'II',
                        iii: 'III',
                        iv: 'IV',
                      }}
                    />
                  </Column>
                </Row>
                <Row>
                  <Column className={styles.driverInfoCol}>
                    <FormInput
                      variant="text"
                      name="contactInfo.homePhone"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={formatPhoneNumber(values.contactInfo.homePhone)}
                      errorMessage={
                        touched.contactInfo?.homePhone
                          ? errors.contactInfo?.homePhone
                          : undefined
                      }
                      label="Home Phone"
                      data-testid="home-phone"
                    />
                  </Column>

                  <Column className={styles.driverInfoCol}>
                    <FormInput
                      variant="text"
                      name="contactInfo.cellPhone"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={formatPhoneNumber(values.contactInfo.cellPhone)}
                      errorMessage={
                        touched.contactInfo?.cellPhone
                          ? errors.contactInfo?.cellPhone
                          : undefined
                      }
                      label="Cell Phone"
                      data-testid="cell-phone"
                    />
                  </Column>

                  <Column
                    className={classNames('fs-exclude', styles.driverInfoCol)}
                  >
                    <FormInput
                      variant="text"
                      type="email"
                      name="contactInfo.email"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.contactInfo.email}
                      errorMessage={
                        touched.contactInfo?.email
                          ? errors.contactInfo?.email
                          : undefined
                      }
                      label="Email Address"
                      data-testid="email"
                    />
                  </Column>
                </Row>
              </div>
            </FormModalContent>
            <FormModalFooter identifier="edit-account" close={close} />
          </>
        );
      }}
    </Formik>
  );
};

EditAccountForm.displayName = 'EditAccountForm';
